<template>
  <div
    :class="{
      'house-card--selected': selected,
      'house-card--disabled': disabled
    }"
    class="house-card"
  >
    <button
      v-if="!disableSelection && (mode === 'available' || mode === 'rented')"
      :class="{ 'house-card__checkbox--selected': selected }"
      :disabled="disabled"
      class="house-card__checkbox"
      type="button"
      @click.stop="toggleSelection"
    >
      <svg v-if="selected" class="house-card__checkbox-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="3" />
      </svg>
    </button>

    <div v-if="mode == 'available'" class="house-card__content">
      <div class="house-card__icon">
        <img :alt="houseData.nameKey" :src="houseData.icon" />
      </div>

      <div class="house-card__main-info">
        <div class="house-card__main-header">
          <div>
            <h3 class="house-card__name">{{ $t(houseData.nameKey) }}</h3>
          </div>
          <UIBadge
            v-if="'profit' in houseData"
            :label="$t('rentHouseCanEarn')"
            :value="Formatters.number.renderString((f) => f.formatNumber(rent.getTotalEarnPerYear(houseData as any)))"
            class="ui-badge ui-badge--hide-label-mobile"
            icon="img/tokens/doubloon.png"
          />
        </div>

        <div class="house-card__progress-controls">
          <div class="house-card__progressbar">
            <UIProgressBar
              v-if="'durability' in houseData"
              :current="houseData.durability"
              :danger-threshold="25"
              :max="100"
              :title="$t('realtyDurability')"
              :warning-threshold="50"
            />
          </div>

          <div class="house-card__btns">
            <HalloweenGenericButton
              :disabled="disabled || (houseData as IUserHouseInterface).durability >= 100 || rootLoading"
              class="house-card__btn"
              @click="handleRepair"
            >
              {{ $t('realtyRepairButton') }}
            </HalloweenGenericButton>
            <HalloweenGenericButton
              :disabled="disabled || (houseData as IUserHouseInterface).durability < 100 || rootLoading"
              class="house-card__btn"
              @click="handleRentOut"
            >
              {{ $t('realtyRentOut') }}
            </HalloweenGenericButton>
          </div>
        </div>
      </div>
    </div>

    <div v-if="mode == 'rented'" class="house-card__content house-card__content--rented">
      <div class="house-card__icon house-card__icon--rented">
        <img :alt="houseData.nameKey" :src="houseData.icon" />
      </div>

      <div class="house-card__main-info">
        <div class="house-card__main-header">
          <div>
            <h3 class="house-card__name">{{ $t(houseData.nameKey) }}</h3>
            <div v-if="'endTimestamp' in houseData" class="house-card__time">
              <UIHalloweenCountdownTimer :target="houseData.endTimestamp" />
              {{ $t('realtyUntilBreaks') }}
            </div>
          </div>
          <div class="tw:flex tw:flex-row tw:gap-2">
            <UIBadge
              v-if="'earned' in houseData"
              :label="$t('rentProfitRate')"
              :value="
                Formatters.number.renderString((f) =>
                  f.formatNumber(rent.getTotalEarnPerYear(houseData as IUserHouseInterface))
                ) + $t('rentHouseCanEarnDay')
              "
              class="ui-badge ui-badge--hide-label-mobile"
              icon="img/tokens/doubloon.png"
            />
            <UIBadge
              v-if="'earned' in houseData"
              :label="$t('realtyEarned')"
              :value="Formatters.number.renderString((f) => f.formatNumber((houseData as IUserHouseInterface).earned))"
              class="ui-badge ui-badge--hide-label-mobile"
              icon="img/tokens/doubloon.png"
            />
          </div>
        </div>

        <div class="house-card__progress-controls house-card__progress-controls--rented">
          <div class="house-card__progressbar">
            <UIProgressBar
              v-if="'durability' in houseData"
              :current="houseData.durability"
              :danger-threshold="25"
              :max="100"
              :title="$t('realtyDurability')"
              :warning-threshold="50"
            />
          </div>

          <div class="house-card__btns">
            <HalloweenGenericButton
              :disabled="disabled || (houseData as IUserHouseInterface).durability >= 100 || rootLoading"
              class="house-card__btn"
              @click="handleRepair"
            >
              {{ $t('realtyRepairButton') }}
            </HalloweenGenericButton>
            <HalloweenGenericButton :disabled="disabled || rootLoading" class="house-card__btn" @click="handleTackBack">
              {{ $t('realtyTackBack') }}
            </HalloweenGenericButton>
          </div>
        </div>
      </div>
    </div>

    <div v-if="mode == 'estate-market'" class="house-card__content">
      <div class="house-card__icon">
        <img :alt="houseData.nameKey" :src="houseData.icon" />
      </div>

      <div class="house-card__main-info">
        <h3 class="house-card__name">{{ $t(houseData.nameKey) }}</h3>

        <div class="house-card__badges-btns">
          <div class="house-card__badges">
            <UIBadge
              v-if="'rentedOutByUser' in houseData && isUserAuthenticated"
              :label="$t('realtyMyRentedHouses')"
              :value="houseData.rentedOutByUser"
              mode="primary"
              value-color="accent"
            />
            <!-- <UIBadge :label="$t('realtyCurrentROI')" value="5.56$" value-color="accent" /> -->
            <UIBadge
              v-if="'buildPrice' in houseData"
              :icon="Tokens.doubloon"
              :label="$t('realtyTotalProfit')"
              :value="
                Formatters.number.renderString((f) =>
                  f.formatUSDNumber((houseData as IMarketHouseInterface).finalProfitPerYear)
                )
              "
              value-color="accent"
            />
            <UIBadge
              v-if="'totalRentedOut' in houseData"
              :label="$t('realtyRentedOut')"
              :value="
                Formatters.number.renderString((f) =>
                  f.formatNumber((houseData as IMarketHouseInterface).totalRentedOut)
                )
              "
              value-color="accent"
            />
          </div>

          <div class="house-card__btns house-card__btns--estate-market">
            <HalloweenGenericButton :disabled="disabled" class="house-card__btn--estate-market" @click="handleBuild">
              {{
                $t(
                  !rent.hasBuildingsType(houseData as IMarketHouseInterface)
                    ? 'realtyButtonBuild'
                    : 'realtyGoToMyBuildings'
                )
              }}
            </HalloweenGenericButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Tokens from 'public/img/tokens';
import {
  type IMarketHouseInterface,
  type IUserHouseInterface,
  RentTab,
  useRentEventStore
} from '~/stores/rent-event-store';
import type { IUserModel } from '~/types/apiService';
import { Formatters } from '~/utils/formatters/formatters';
import HalloweenGenericButton from '../Halloween/HalloweenGenericButton.vue';
import UIHalloweenCountdownTimer from '../Halloween/ui/UIHalloweenCountdownTimer.vue';

const { t } = useI18n();
const { data: userData } = useNuxtData<IUserModel>('userData');
const isUserAuthenticated = computed(() => !!userData.value);
const rent = useRentEventStore();

export interface PropertyBuildingCardProps {
  /** Режим отображения компонента */
  mode: 'available' | 'rented' | 'estate-market';
  houseData: IUserHouseInterface | IMarketHouseInterface;
  selected?: boolean;
  disableSelection?: boolean;
  disabled?: boolean;
  rootLoading?: boolean;
}
const {
  selected = false,
  disableSelection = false,
  disabled = false,
  houseData
} = defineProps<PropertyBuildingCardProps>();

console.log('zzzx', houseData);

const emit = defineEmits<{
  'update:selected': [value: boolean];
  repair: [selectedHouses: IUserHouseInterface];
  'rent-out': [selectedHouses: IUserHouseInterface];
  'tack-back': [selectedHouses: IUserHouseInterface];
  build: [actionName: string];
}>();

const toggleSelection = () => {
  if (!disableSelection && !disabled) {
    emit('update:selected', !selected);
  }
};

const handleRepair = () => {
  if (!disabled) {
    emit('repair', houseData as IUserHouseInterface);
  }
};

const handleRentOut = () => {
  if (!disabled) {
    emit('rent-out', houseData as IUserHouseInterface);
  }
};

const handleTackBack = () => {
  if (!disabled) {
    emit('tack-back', houseData as IUserHouseInterface);
  }
};

const handleBuild = () => {
  if (!disabled) {
    if (rent.hasBuildingsType(houseData as IMarketHouseInterface)) {
      rent.$state.selectedTab = RentTab.AvailableHouses;
      setTimeout(() => {
        rent.$state.selectedHouses = rent.$state.userHouses.filter(
          (h) =>
            !h.rentedOut &&
            h.durability >= 100 &&
            h.address === (houseData as IMarketHouseInterface).nftAddress &&
            h.buildingType === houseData.buildingType
        );
      }, 100);
    } else {
      emit('build', t('realtyButtonBuild'));
    }
  }
};
</script>

<style lang="scss" scoped>
.house-card {
  position: relative;
  background: radial-gradient(92.3% 92.29% at 50% -7.05%, rgba(0, 131, 173, 0.15) 0%, rgba(0, 0, 0, 0) 100%),
    radial-gradient(59.02% 71.08% at 84.01% 85.82%, #040f14 0%, #081b25 100%);

  border: 1px solid #1e353e;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;

  &--selected {
    box-shadow: 0 0 0px 2px #039fd0;
  }

  &__checkbox {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 14px;
    height: 14px;
    border: 1px solid #4c4d4f;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    :hover {
      border-color: #039fd0;
    }

    &--selected {
      background: #039fd0;
      border-color: #fff;
    }
  }

  &__checkbox-mark {
    color: white;
    font-size: 16px;
    font-weight: bold;
  }

  &__checkbox-icon {
    color: #fff;
  }

  &__content {
    display: flex;
    gap: 16px;
    width: 100%;
  }

  &__main-info {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;
  }

  &__badges-btns {
    display: flex;
    align-items: center;
    margin-top: 16px;
    gap: 30px;
  }

  &__badges {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    width: 100%;
  }

  &__main-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 14px 0;
  }

  &__progress-controls {
    display: flex;
    align-items: center;
    gap: 46px;
  }

  &__progressbar {
    flex-grow: 1;
    width: 100%;
  }

  &__btns {
    display: flex;
    align-items: center;
    gap: 16px;
    justify-content: flex-end;
  }

  &__btn {
    height: 32px;
    width: max-content;
    padding: 10px;
  }

  &__icon {
    flex-shrink: 0;
    width: 75px;
    height: auto;
    margin: auto 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 10px;
    }
  }

  &__name {
    color: white;
    font-size: 24px;
    font-weight: bold;
    margin: 0;
    max-width: 500px;
  }

  &__time {
    font-weight: 700;
    font-size: 16px;
    color: #9ec7d4;
    display: flex;
    align-items: center;
    gap: 5px;
  }
}

@media screen and (max-width: 768px) {
  .house-card {
    position: relative;
    padding: 10px;

    &__progress-controls {
      display: flex;
      flex-direction: column;
      align-items: end;
      gap: 20px;

      &--rented {
        gap: 20px;
      }
    }

    &__main-info {
      gap: 8px;
    }

    &__main-header {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin: 0;
    }

    &__name {
      max-width: 170px;
      font-size: 18px;
    }

    &__icon {
      &--rented {
        margin: 10px 0 auto 0;
      }
    }

    &__content {
      &--rented {
        gap: 16px;
      }
    }

    &__time {
      width: 100%;
      text-align: end;
    }

    &__badges {
      align-items: flex-start;
      gap: 8px;
    }

    &__badges > * {
      min-width: 0;
      max-width: 100%;
      box-sizing: border-box;

      display: inline-flex;
      flex-direction: column;
      align-items: flex-start;
      white-space: normal;
      gap: 8px;
    }

    &__badges > * :first-child {
      display: block;
      white-space: normal;
    }

    &__badges-btns {
      flex-direction: column;
    }

    &__btns {
      &--estate-market {
        justify-content: flex-end;
        width: 100%;
      }
    }

    &__btn {
      &--estate-market {
        height: 32px;
        padding: 10px;
      }
    }
  }

  .ui-badge--hide-label-mobile {
    :deep(.badge__label) {
      display: none;
    }
  }
}
</style>
